import React, { Dispatch, SetStateAction, useCallback } from "react";
import {
  Permission,
  RemoveAccessData,
  ResourceType,
} from "components/shareSection/entities";
import { UserModelRole, SharedAccess } from "target_wasm";
import {
  fetchModelSharedWithList,
  handleModelRevokeAccess,
  handleShareModel,
} from "components/main/utils/models/ModelServices";
import { resendInvitation } from "@/lib/api/resendInvitation";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { ManageAccessDialog } from "components/shareSection/ManageAccessDialog";
import { useSharedAccessManager } from "components/shareSection/useSharedAccessManager";

interface ShareModelProps {
  modelID: string;
  modelName: string;
  role: UserModelRole | undefined;
  sharedWith: SharedAccess | undefined;
  setIsShare: () => void;
  canManageAccess: boolean;
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>;
  onAfterRevoke?: (data: RemoveAccessData | null) => Promise<void>;
}

export const ShareModel: React.FC<ShareModelProps> = (props) => {
  const userCurrentEmail = localStorage.getItem("email");
  const clientApp = useBoundStore((state) => state.clientApp);
  const bumpDetailVersion = useBoundStore((state) => state.bumpDetailVersion);

  const refreshModelPermissions = useCallback(() => {
    clientApp?.fetch_model_permissions();
  }, [clientApp]);

  const manager = useSharedAccessManager({
    entityId: props.modelID,
    setSharedWithList: props.setSharedWithList,
    fetchFn: fetchModelSharedWithList,
    onPermissionError: async () => {
      // Trigger re-render to re-read model permissions from WASM cache
      bumpDetailVersion();
    },
    onAccessChanged: refreshModelPermissions,
  });

  const handleShareModelAction = useCallback(
    async (
      emails: string[],
      roleOrPermission: UserModelRole | Permission | null,
    ) => {
      const matchingAccesses =
        props.sharedWith
          ?.users()
          .filter(
            (userAccess) =>
              userAccess.user_js().user_email_js() === userCurrentEmail,
          ) || [];
      const matchingUserName =
        matchingAccesses?.[0]?.user_js()?.user_name_js() ?? "Unknown";

      await handleShareModel(
        matchingUserName,
        props.modelName,
        emails,
        roleOrPermission as UserModelRole,
        props.modelID as string,
        () => {},
        () => {},
        props.setSharedWithList,
        String(userCurrentEmail),
      );
    },
    [
      props.modelName,
      props.modelID,
      props.setSharedWithList,
      props.sharedWith,
      userCurrentEmail,
    ],
  );

  const handleRevokeAccessModel = useCallback(
    async (data: RemoveAccessData | null) => {
      await handleModelRevokeAccess(
        props.modelID,
        data,
        props.setSharedWithList,
        props.modelName,
      );
      await props.onAfterRevoke?.(data);
    },
    [
      props.modelID,
      props.modelName,
      props.setSharedWithList,
      props.onAfterRevoke,
    ],
  );

  const handleResendInvitation = useCallback(
    async (email: string) => {
      const matchingAccesses =
        props.sharedWith
          ?.users()
          .filter(
            (userAccess) =>
              userAccess.user_js().user_email_js() === userCurrentEmail,
          ) || [];
      const inviterName =
        matchingAccesses?.[0]?.user_js()?.user_name_js() ?? "Unknown";

      await resendInvitation(
        "model",
        props.modelID,
        props.modelName,
        email,
        inviterName,
      );
    },
    [props.modelID, props.modelName, props.sharedWith, userCurrentEmail],
  );

  return (
    <ManageAccessDialog
      open={true}
      onClose={props.setIsShare}
      entityId={props.modelID}
      resourceType={ResourceType.Model}
      sharedWithList={props.sharedWith}
      setSharedWithList={props.setSharedWithList}
      canShare={props.canManageAccess}
      canRevokeAccess={props.canManageAccess}
      onShare={handleShareModelAction}
      onRevokeAccess={handleRevokeAccessModel}
      onSuccess={manager.handleSuccess}
      onError={manager.handleError}
      defaultRole={UserModelRole.Collaborator}
      onRefetchSharedList={manager.handleRefetchSharedList}
      userContinuationToken={manager.userContinuationToken}
      modelName={props.modelName}
      onResendInvitation={handleResendInvitation}
    />
  );
};
